import moment, { Moment } from 'moment'
import _ from 'underscore'
import Brand from 'helpers/brand'

import Environment from 'helpers/environment'

import Honeybadger from 'honeybadger-js'

export const precision = (value: number, precision: number) => scrubNonNumber(value, () => formatNumericalUnit(value, '', precision))
export const defaultIfOutrageous = (value: number | string | null | undefined) => scrubNonNumber(value)

/* eslint-disable no-multi-spaces */
const unitFormats = [
  { name: 'amps',                    unit: 'A',     spaceBetween: true,  defaultPrecision: 1 },
  { name: 'cubicFeetPerMinute',      unit: 'CFM',   spaceBetween: true                       },
  { name: 'degrees',                 unit: '°',     spaceBetween: false                      },
  { name: 'degreesFahrenheit',       unit: '°F',    spaceBetween: false, defaultPrecision: 1 },
  { name: 'feet',                    unit: 'ft',    spaceBetween: true                       },
  { name: 'microgramsPerCubicMeter', unit: 'µg/m³', spaceBetween: true,  defaultPrecision: 1 },
  { name: 'partsPerBillion',         unit: 'ppb',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'partsPerMillion',         unit: 'ppm',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'percentage',              unit: '%',     spaceBetween: false                      },
  { name: 'pressure',                unit: 'PSIG',  spaceBetween: true,  defaultPrecision: 1 },
  { name: 'relativeHumidity',        unit: '% RH',  spaceBetween: false, defaultPrecision: 0 },
  { name: 'rpm',                     unit: 'RPM',   spaceBetween: true                       },
  { name: 'rps',                     unit: 'RPS',   spaceBetween: true                       },
  { name: 'staticPressure',          unit: 'inWC',  spaceBetween: true,  defaultPrecision: 2 },
  { name: 'voltageAC',               unit: 'VAC',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'voltageDC',               unit: 'VDC',   spaceBetween: true,  defaultPrecision: 1 },
  { name: 'watts',                   unit: 'W',     spaceBetween: true,  defaultPrecision: 1 },
  { name: 'number',                  unit: '',      spaceBetween: false                      }
]
/* eslint-enable no-multi-spaces */

export const formatForUnit = (value: number | string | null | undefined, unit: string, precision: number | null | undefined = null) => {
  const format = unitFormats.find(f => f.unit === unit)

  if (format) {
    return scrubNonNumber(value, () => {
      const precisionToUse = precision ?? format.defaultPrecision ?? 0
      const unitToUse = format.spaceBetween ? ` ${unit}` : unit

      return formatNumericalUnit(value as number | string, unitToUse, precisionToUse)
    })
  } else {
    if (!Environment.isDevelopment()) {
      Honeybadger.notify(`No formatter found for unit: ${unit}`)
      return defaultIfOutrageous(value)
    } else {
      throw new Error(`No formatter found for unit: ${unit}`)
    }
  }
}

const buildUnitFormatter = (name: string) => (value: number | string | null | undefined, precision?: number | null) => formatForUnit(value, unitFormats.find(f => f.name === name)!.unit, precision)

export const amps = buildUnitFormatter('amps')
export const cubicFeetPerMinute = buildUnitFormatter('cubicFeetPerMinute')
export const degrees = buildUnitFormatter('degrees')
export const degreesFahrenheit = buildUnitFormatter('degreesFahrenheit')
export const feet = buildUnitFormatter('feet')
export const microgramsPerCubicMeter = buildUnitFormatter('microgramsPerCubicMeter')
export const partsPerBillion = buildUnitFormatter('partsPerBillion')
export const partsPerMillion = buildUnitFormatter('partsPerMillion')
export const percentage = buildUnitFormatter('percentage')
export const pressure = buildUnitFormatter('pressure')
export const relativeHumidity = buildUnitFormatter('relativeHumidity')
export const rpm = buildUnitFormatter('rpm')
export const rps = buildUnitFormatter('rps')
export const staticPressure = buildUnitFormatter('staticPressure')
export const voltageAC = buildUnitFormatter('voltageAC')
export const voltageDC = buildUnitFormatter('voltageDC')
export const watts = buildUnitFormatter('watts')
export const number = buildUnitFormatter('number')

export const damperAngle = (value: number | string | null | undefined) => scrubNonNumber(value, () => formatNumericalUnit(((100 - Number(precision(value as number, 0))) / 100) * 90, 'deg'))
export const onOff = (value: boolean | null | undefined) => scrubNull(value, () => { if (value === true) { return 'on' } else { return 'off' } })
export const ratioToPercent = (value: number | string | null | undefined) => scrubNonNumber(value, () => value ? (percentage(Number(value) * 100) as string | null | undefined) : (value as string | null | undefined))
export const rpmToPwm = (value: number | string | null | undefined) => scrubNonNumber(value, () => formatNumericalUnit(Math.round(((Number(value) - 200) / 13.3) + 20), ' PWM', 1))
export const rpsToRpm = (value: number | string | null | undefined) => scrubNonNumber(value, () => formatNumericalUnit(Number(value) * 60, ' RPM', 0))

export const longDate = (date: string | Date) => {
  return (
    new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
      .format(moment(date, 'YYYY-MM-DD').toDate())
  )
}

export const longDateTime = (dateTime: Date | Moment | string | number) => moment(dateTime).format('llll')
export const shortTimeDate = (dateTime: Date| Moment | string | number) => moment(dateTime).format('h:mm a M/D/YYYY')
export const shortDateTime = (dateTime: Date| Moment | string | number) => moment(dateTime).format('MM/DD/YYYY h:mm A') // this has a non-breaking space before "A" so the time stays together
export const shortTime = (dateTime: Date| Moment | string | number) => moment(dateTime).format('h:mm A')
export const shortDate = (dateTime: Date| Moment | string | number) => moment(dateTime).format('MM/DD/YYYY')
export const longMonthYear = (dateTime: Date| Moment | string | number) => moment(dateTime).format('MMMM YYYY')
export const longDateTimeWithSeconds = (dateTime: Date| Moment | string | number) => moment(dateTime).format('ddd, MMM D, YYYY h:mm:ss A')
export const shortDateWithDayOfWeek = (dateTime: Date| Moment | string | number) => moment(dateTime).format('ddd, MMM D, YYYY')
export const shortNumericDateWithDayOfWeek = (dateTime: Date| Moment | string | number) => moment(dateTime).format('ddd M/D/YYYY')
export const shortMonthDay = (dateTime: Date| Moment | string | number) => moment(dateTime).format('MMM D')
export const longDateWithDayOfWeekAndTime = (dateTime: Date| Moment | string | number) => moment(dateTime).format('dddd, MMM D, YYYY h:mm:ss A')
export const monthFullName = (dateTime: Date| Moment | string | number) => moment(dateTime).format('MMMM')

export const customerDisplayName = (firstName: string | null | undefined, lastName: string | null | undefined, companyName: string | null | undefined) => {
  if (Brand.isCommercial() && companyName) {
    return companyName
  } else {
    return _.compact([firstName, lastName]).join(' ')
  }
}

export const deviceTypeDisplayName = (deviceType: string | null | undefined) => {
  return deviceType === 'control_hub' ? 'system controller' : deviceType?.replace(/_/g, ' ')
}

export const phoneNumber = (number: string | null | undefined) => {
  if (!number) return null

  if (number?.length === 10) {
    return number?.replace(/(\d\d\d)(\d\d\d)(\d\d\d\d)/, '($1) $2-$3') ?? ''
  } else if (number?.length === 11) {
    return number?.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4')
  } else {
    return number
  }
}

export const streetAddress = (address1: string | null | undefined, address2: string | null | undefined) => {
  return _.compact([address1, address2]).join(', ')
}

export const cityStateZip = (city: string | null | undefined, state: string | null | undefined, zip: string | null | undefined) => {
  return _.compact([city, _.compact([state, zip]).join(' ')]).join(', ')
}

export const fullOneLineAddress = (address: string | null | undefined, city: string | null | undefined, state: string | null | undefined, zip: string | null | undefined) => {
  return _.compact([address, city, _.compact([state, zip]).join(' ')]).join(', ')
}

export const friendlyName = (name: string | number | null | undefined, friendlyNames: Record<string, string | number> = {}) => {
  if (name != null && Object.keys(friendlyNames).includes(String(name))) {
    return friendlyNames[name]
  } else {
    return replace(name != null ? String(name) : name)
  }
}

export const replace = (str: string | null | undefined, find: string = '_', replace: string = ' ') => {
  return scrubNull(str, () => String(str).replace(new RegExp(find, 'g'), replace))
}

const formatNumericalUnit = (value: number | string, symbol: string, precision: number = 1) => {
  return Number(value).toFixed(precision) + symbol
}

const scrubNonNumber = (value: number | string | null | undefined, formatter: ((v: number | string | null | undefined) => string | null | undefined) | null = null) => {
  if (scrubNull(value) === null || isNaN(Number(value)) || Number(value) === 32768 || Number(value) === 3276800) {
    return null
  } else if (formatter) {
    return formatter(value)
  }

  return value
}

export const scrubNull = (value: unknown, formatter: ((v: unknown) => string | null | undefined) | null = null): string | null | undefined => {
  if (value === undefined || value === null || value === '') {
    return null
  } else if (formatter) {
    return formatter(value)
  }

  return value as string | null | undefined
}

export const removeScriptTags = (text:string) => {
  const div = document.createElement('div')
  div.innerHTML = text

  const scripts = div.getElementsByTagName('script')
  let i = scripts.length

  while (i--) {
    scripts[i].parentNode?.removeChild(scripts[i])
  }

  return div.innerHTML
}
export const generateUniqueId = () => `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

export const capitalize = (string: string | null | undefined) => {
  if (!string || string === '') return string
  return string
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export const camelToNormalCase = (string: string | null | undefined) => {
  if (!string || string === '') {
    return string
  } else {
    return string
      .split(/(?=[A-Z])/)
      .map(word => capitalize(word))
      .join(' ')
  }
}

export const installationStatus = (status: boolean) => status ? 'INSTALLED' : 'NOT INSTALLED'

export const formatStages = (stage: string) => {
  if (stage?.includes(' ')) return stage
  const match = stage?.match(/^stage(\d+)$/)
  if (match) return `${match[1]} Stage`
  return camelToNormalCase(stage) || undefined
}

export const mapToDropdownOptions = (map: Record<string, string | number | null>) =>
  Object.keys(map).map((key) => [{ value: String(map[key]).trim(), name: replace(key, '_', ' ') }])

export const isValidNumericInput = (value: string, maxDigits: number) =>
  new RegExp(`^\\d{1,${maxDigits}}(\\.\\d)?$`).test(value)

export const getErrorMessage = (label: string, value: string | number | null | undefined) => value ? undefined : `${label} is required`
